import React, { useState, useContext } from "react";
import PropTypes from "prop-types";

import CommonDialog from "components/CommonDialog";
import { CompanyDetails } from "components";
import { USER_SERVICES } from "Services";
import { Context } from "context";
import { ADD_NOTIFICATION } from "context/actions";
import Utils from "Shared/Utils";

const CompanyIdentityModal = (props) => {
  const {
    data,
    isOpen,
    closePopup,
    onUpdate,
    countryConfig,
  } = props;

  const {
    level_data: levelData = {},
    settings: settingsData = {},
    account: accountData = {},
  } = data;

  const {
    partner_id,
    group_id,
    region_id,
    location_id,
    pool_client_id
  } = levelData;
  const {
    company: {
      name: legalName,
    } = {}
  } = accountData;
  const {
    setting_level: level,
    custom_onboard = false,
    fee_schedule_desc = "",
    plan_summary_desc = ""
  } = settingsData;

  const { dispatch: globalDispatch } = useContext(Context);

  const isGroup = level === "group";
  const isRegionLocation = (level === "region" || level === "location");
  const isLocation = level === "location";
  const isRegion = level === "region";

  const [companyDetail, setCompanyDetail] = useState({
    companyName: levelData?.name || "",
    displayName: levelData?.location_display_name || "",
    companyLegalName: legalName || levelData?.legal_name || "",
    industry: levelData?.industry || "",
    billing_npi: levelData?.billing_npi || "",
    ext_facility_id: levelData?.ext_facility_id || "",
    website: levelData?.plan_website || "",
    patient_website: levelData?.patient_website || "",
    patient_url_part: levelData?.patient_url_part || "",
    addressLine: levelData?.address?.line1 || "",
    addressLine2: levelData?.address?.line2 || "",
    status: levelData?.status || "",
    city: levelData?.address?.city || "",
    state: levelData?.address?.state || "",
    phone: Utils.formatPhoneWithCountryCode(levelData?.phone, countryConfig) || "",
    email: levelData?.email || "",
    zipCode: levelData?.address?.postal_code || levelData?.address?.zipcode || "",
    latitude: levelData?.latitude || "",
    longitude: levelData?.longitude || "",
    slug: levelData?.slug || "",
    fee_schedule_desc: fee_schedule_desc,
    plan_summary_desc: plan_summary_desc
  });
  const [checkErrors, setCheckErrors] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [editDetails, setEditDetails] = useState(false);
  const [blurred, setBlurred] = useState({});

  const handleInputChange = (value, attribute, mapUpdates) => {
    if (mapUpdates)
      setCompanyDetail({ ...companyDetail, ...mapUpdates });
    else
      setCompanyDetail({ ...companyDetail, [attribute]: value });

    setEditDetails(true);
  };

  const checkErrorsInForm = async () => {
    const fieldsWithErrors = {};
    let hasErrors = false;

    if (!companyDetail.companyName) {
      fieldsWithErrors.companyName = true;
      hasErrors = true;
    }

    if (!companyDetail.addressLine) {
      fieldsWithErrors.addressLine = true;
      hasErrors = true;
    }

    if (!companyDetail.city) {
      fieldsWithErrors.city = true;
      hasErrors = true;
    }

    if (countryConfig?.code === 'US' && !companyDetail.state) {
      fieldsWithErrors.state = true;
      hasErrors = true;
    }

    if (!Utils.validateZipCode(companyDetail.zipCode, countryConfig)) {
      fieldsWithErrors.zipCode = true;
      hasErrors = true;
    }

    if (!Utils.validateMobile(companyDetail.phone, countryConfig?.code)) {
      fieldsWithErrors.phone = true;
      hasErrors = true;
    }

    if (!Utils.validateEmail(companyDetail.email)) {
      fieldsWithErrors.email = true;
      hasErrors = true;
    }

    if (!Utils.validateWebsite(companyDetail.website)) {
      fieldsWithErrors.website = true;
      hasErrors = true;
    }

    if (isGroup && !companyDetail.fee_schedule_desc) {
      fieldsWithErrors.fee_schedule_desc = true;
      hasErrors = true;
    }

    if (isGroup && !companyDetail.plan_summary_desc) {
      fieldsWithErrors.plan_summary_desc = true;
      hasErrors = true;
    }

    if (!companyDetail.companyLegalName && (custom_onboard || legalName)) {
      fieldsWithErrors.companyLegalName = true;
      hasErrors = true;
    }

    if (isLocation) {
      if (
        companyDetail.billing_npi &&
        !Utils.validateNPI(companyDetail.billing_npi)
      ) {
        fieldsWithErrors.billing_npi = true;
        hasErrors = true;
      }

      if (!companyDetail.status) {
        fieldsWithErrors.status = true;
        hasErrors = true;
      }

      if (companyDetail.slug?.toLowerCase() === 'thank-you') {
        fieldsWithErrors.slug = true;
        hasErrors = true;
      }

      if (
        (companyDetail.ext_facility_id || custom_onboard) &&
        !Utils.validateFacilityId(companyDetail.ext_facility_id)
      ) {
        fieldsWithErrors.ext_facility_id = true;
        hasErrors = true;
      }
    }
    if (isRegion && companyDetail.slug && !Utils.validateSlug(companyDetail.slug)) {
      fieldsWithErrors.slug = true;
      hasErrors = true;
    }
    if (isGroup) {
      const validWebsite = Utils.validateWebsite(companyDetail.patient_website);
      if (!validWebsite) {
        fieldsWithErrors.patient_website = true;
        hasErrors = true;
      }
    }

    if (isGroup && !companyDetail?.patient_url_part) {
      fieldsWithErrors.patient_url_part = true;
      hasErrors = true;
    }

    if (!isRegionLocation && !companyDetail.industry) {
      fieldsWithErrors.industry = true;
      hasErrors = true;
    }

    if (hasErrors) {
      setCheckErrors(true);
      setBlurred({ ...blurred, ...fieldsWithErrors });
      return true;
    }

    return false;
  };

  const onConfirmClick = async () => {
    let hasErrors = await checkErrorsInForm();
    if (hasErrors)
      return;

    if (!editDetails) {
      closePopup();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        [level]: {
          location_id,
          region_id,
          group_id,
          partner_id,
          [`${level}_name`]: companyDetail.companyName,
          legal_name: companyDetail.companyLegalName,
          plan_website: companyDetail.website,
          address: {
            line1: companyDetail.addressLine,
            line2: companyDetail.addressLine2,
            city: companyDetail.city,
            state: companyDetail?.state,
            zipcode: companyDetail.zipCode,
            country_code: countryConfig?.code
          },
          phone: Utils.formatPhoneWithCountryCode(companyDetail.phone, countryConfig, true),
          email: companyDetail.email,
        },
      };

      if (!isRegionLocation) {
        payload[level].industry = companyDetail.industry;
      }

      if (isLocation) {
        payload[level].latitude = companyDetail.latitude;
        payload[level].longitude = companyDetail.longitude;
        payload[level].status = companyDetail.status;
        payload[level].location_display_name = companyDetail.displayName;
        payload[level].slug = companyDetail.slug;
      }

      if (isRegion) {
        // Null clears a slug that was set. A region that never had one sends nothing.
        if (companyDetail.slug) payload[level].slug = companyDetail.slug;
        else if (levelData?.slug) payload[level].slug = null;
      }

      if (isLocation && countryConfig?.code === 'US') {
        payload[level].billing_npi = companyDetail.billing_npi;
        payload[level].ext_facility_id = companyDetail.ext_facility_id;
      }

      if (isGroup || isLocation) {
        payload['setting'] = {
          fee_schedule_desc: companyDetail.fee_schedule_desc,
        };
      }

      if (isGroup) {
        payload[level].patient_website = companyDetail.patient_website;
        payload[level].patient_url_part = companyDetail.patient_url_part;
        payload['setting'].plan_summary_desc = companyDetail.plan_summary_desc;
      }

      const response = await USER_SERVICES.updateLevels(payload, level);

      if (response?.type === 'success') {
        dispatchGlobalNotification('success', "Details updated successfully");
        closePopup();
        onUpdate("Settings");
      }
      else {
        throw (response?.message || response?.error);
      }
    } catch (error) {
      dispatchGlobalNotification('error', error || "Error updating the details");
    }
    setLoading(false);
  };

  const dispatchGlobalNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } }
    });

  return (
    <CommonDialog
      open={isOpen}
      handleClose={closePopup}
      dialogTitle="Update Company Details"
      primaryBtnTxt="Confirm"
      handlePrimaryButtonClick={onConfirmClick}
      disabledPrimaryButton={isLoading}
      primaryButtonStyles={{ minWidth: 105 }}
      isLoading={isLoading}
      secondaryBtnTxt="Cancel"
      disableSecondaryButton={isLoading}
    >
      <CompanyDetails
        checkErrors={checkErrors}
        companyDetail={companyDetail}
        handleTextChange={handleInputChange}
        level={level}
        data={data}
        legalName={legalName}
        blurred={blurred}
        setBlurred={setBlurred}
        countryConfig={countryConfig}
      />
    </CommonDialog>
  );
};

CompanyIdentityModal.propTypes = {
  data: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closePopup: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  countryConfig: PropTypes.object
};

export default CompanyIdentityModal;

